import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import LoadingComponent from "../../app/layout/LoadingComponent";
import {
  fetchSiteAsync,
  resetOpenApiUpload,
  siteSelectors,
  uploadOpenAPISchema
} from "./catalogSlice";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import Layout from "../../app/layout/Layout";
import { useNavigate } from "react-router-dom";
import { EuiBreadcrumb, EuiBreadcrumbs, EuiSpacer } from "@elastic/eui";
import BackButton from "../../app/layout/BackButton";

export default function UploadOpenAPIScheme() {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const site = useAppSelector((state) => siteSelectors.selectById(state, id!));
  const { status: siteStatus, openApiUploadStatus, openApiUploadError } = useAppSelector(
    (state) => state.catalog
  );
  const [file, setFile] = useState<File | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!site) dispatch(fetchSiteAsync(id!));
  }, [id, site, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    dispatch(resetOpenApiUpload());
  };

  const handleUpload = () => {
    if (file) {
      dispatch(uploadOpenAPISchema({ file, siteId: id! }));
    }
  };

  const breadcrumbs: EuiBreadcrumb[] = [
    {
      text: "Websites",
      href: "catalog/",
      onClick: (e) => e.preventDefault(),
      "data-test-subj": "breadcrumbsAnimals"
    },
    {
      text: site?.originName || "Site",
      href: "/",
      onClick: (e) => e.preventDefault()
    },
    {
      text: "Upload OpenAPI Schema",
      href: "#",
      onClick: (e) => e.preventDefault()
    }
  ];

  if (siteStatus.includes("pending")) {
    return <LoadingComponent message="Loading website..." />;
  }

  if (!site) return <h3>Website not found</h3>;

  return (
    <Layout>
      <BackButton />
      <EuiBreadcrumbs
        breadcrumbs={breadcrumbs}
        truncate={false}
        aria-label="OpenAPI Upload Breadcrumbs"
      />
      <EuiSpacer size="xl" />

      <Typography variant="h4" mb={2}>
        Upload OpenAPI Definition
      </Typography>

      <Typography mb={4}>
        Upload your <code>.yaml</code> or <code>.json</code> OpenAPI definition file to analyze API coverage for this website.
      </Typography>

      <Box maxWidth={500} mx="auto" p={4}>
        <Typography variant="h5" gutterBottom>
          Select and Upload File
        </Typography>

        <Stack spacing={2}>
          <input
            type="file"
            accept=".json,.yaml,.yml"
            onChange={handleChange}
            style={{ display: "none" }}
            id="upload-button"
          />
          <label htmlFor="upload-button">
            <Button variant="outlined" component="span" fullWidth>
              {file ? file.name : "Select OpenAPI File"}
            </Button>
          </label>

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!file || openApiUploadStatus === "pending"}
            fullWidth
          >
            {openApiUploadStatus === "pending" ? "Uploading..." : "Upload"}
          </Button>

          {openApiUploadStatus === "pending" && <LinearProgress />}

          {openApiUploadStatus === "succeeded" && (
            <Alert severity="success">Upload successful!</Alert>
          )}

          {openApiUploadStatus === "failed" && (
            <Alert severity="error">Error: {openApiUploadError}</Alert>
          )}
        </Stack>
      </Box>
    </Layout>
  );
}